import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { SeverityBadge } from '../components/SeverityBadge';
import api, { API_BASE } from '../lib/api';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Download, Mail, CheckCircle2, XCircle, MinusCircle, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

const SCORE_BANDS = [
  { min: 85, color: 'text-severity-pass', label: 'Strong' },
  { min: 70, color: 'text-gradient-pt', label: 'Acceptable' },
  { min: 50, color: 'text-gradient-pt', label: 'Needs work' },
  { min: 0, color: 'text-severity-critical', label: 'High risk' },
];

const ScoreGauge = ({ value }) => {
  const band = SCORE_BANDS.find((b) => value >= b.min) || SCORE_BANDS[3];
  const pct = Math.max(0, Math.min(100, value || 0));
  // gauge bar gradient when score in middle bands, solid critical/pass at extremes
  const useGradient = pct >= 50 && pct < 85;
  return (
    <div className="flex items-end gap-6" data-testid="overall-score">
      <div>
        <div className="label-mono mb-1">Overall score</div>
        <div className={`font-medium text-7xl leading-none tabular ${band.color}`}>{pct}<span className="text-2xl text-muted-foreground">/100</span></div>
        <div className={`mt-2 label-mono ${band.color}`}>{band.label}</div>
      </div>
      <div className="flex-1 hidden md:block">
        <div className="h-2 bg-border relative">
          <div
            className={useGradient ? 'absolute inset-y-0 left-0 gradient-pt' : `absolute inset-y-0 left-0 ${band.color}`}
            style={{ width: `${pct}%`, backgroundColor: useGradient ? undefined : 'currentColor' }}
          />
        </div>
      </div>
    </div>
  );
};

const Counts = ({ a }) => {
  const items = [
    { sev: 'CRITICAL', v: a.critical_count },
    { sev: 'HIGH', v: a.high_count },
    { sev: 'MEDIUM', v: a.medium_count },
    { sev: 'LOW', v: a.low_count },
    { sev: 'PASS', v: a.passed_count },
  ];
  return (
    <div className="grid grid-cols-5 border border-border" data-testid="severity-counts">
      {items.map((it) => (
        <div key={it.sev} className="p-4 border-r border-border last:border-r-0">
          <SeverityBadge severity={it.sev} />
          <div className="font-mono text-3xl mt-3">{it.v ?? 0}</div>
        </div>
      ))}
    </div>
  );
};

const MandatoryRow = ({ label, status }) => {
  let icon = <MinusCircle size={14} className="text-muted-foreground" />;
  if (status?.present === true) icon = <CheckCircle2 size={14} className="text-severity-pass" />;
  if (status?.present === false) icon = <XCircle size={14} className="text-severity-critical" />;
  return (
    <div className="flex items-center justify-between py-3 border-b border-border last:border-b-0 text-sm">
      <div className="flex items-center gap-3">{icon}<span>{label}</span></div>
      <div className="font-mono text-xs text-muted-foreground">{JSON.stringify(status || {})}</div>
    </div>
  );
};

export default function AuditReport() {
  const { id } = useParams();
  const [audit, setAudit] = useState(null);
  const [email, setEmail] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get(`/audit/${id}`).then(({ data }) => setAudit(data)).catch(() => {});
  }, [id]);

  if (!audit) {
    return (
      <Layout>
        <div className="mx-auto max-w-[1400px] px-4 sm:px-6 md:px-10 py-20">
          <div className="dot-loader"><span /><span /><span /></div>
        </div>
      </Layout>
    );
  }

  const r = audit.audit_result || {};
  const flags = r.flags || [];
  const passed = r.passed_checks || [];
  const m = r.mandatory_fields_status || {};

  const downloadPdf = () => {
    const token = localStorage.getItem('pc_session_token') || '';
    const url = `${API_BASE}/audit/${id}/report/pdf${token ? `?_t=${Date.now()}` : ''}`;
    fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {}, credentials: 'include' })
      .then((res) => res.blob())
      .then((blob) => {
        const a = document.createElement('a');
        const objUrl = URL.createObjectURL(blob);
        a.href = objUrl;
        a.download = `PackCheck-${id}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(objUrl);
      })
      .catch(() => toast.error('Could not download report'));
  };

  const emailReport = async () => {
    if (!email) { toast.error('Enter an email'); return; }
    setSending(true);
    try {
      const { data } = await api.post(`/audit/${id}/report/email`, { to_email: email });
      if (data.status === 'sent') toast.success(`Sent to ${email} from ${data.sender || 'your Gmail'}`);
      else if (data.status === 'skipped') {
        toast.error('Your Gmail is not connected. Open Account → Email delivery → Connect Gmail to enable sending.', { duration: 8000 });
      }
      else toast.error(`Send failed: ${data.error || 'unknown'}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Send failed');
    } finally {
      setSending(false);
    }
  };

  return (
    <Layout>
      <div className="mx-auto max-w-[1400px] px-4 sm:px-6 md:px-10 py-10" data-testid="audit-report">

        <Link to="/dashboard" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-6"><ArrowLeft size={14} strokeWidth={1.5} /> Back to dashboard</Link>

        {/* HEADER */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10 border-b border-border pb-12">
          <div className="lg:col-span-2 space-y-3">
            <div className="label-mono">PackCheck / Audit report</div>
            <h1 className="text-3xl md:text-4xl font-medium tracking-tight">{audit.file_name}</h1>
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-muted-foreground">
              <span className="font-mono">{audit.audit_id}</span>
              <span>{audit.pack_config?.pack_format_name} <span className="font-mono text-xs">{audit.pack_config?.pack_format_id}</span></span>
              <span className="capitalize">Level · {audit.pack_config?.packaging_level}</span>
              <span>Pages · {audit.page_count}</span>
            </div>
            <p className="mt-4 text-base text-muted-foreground leading-relaxed max-w-3xl">{r.summary}</p>
          </div>
          <div className="space-y-6">
            <ScoreGauge value={audit.overall_score || r.overall_score || 0} />
            <div className="flex flex-col gap-2">
              <Button onClick={downloadPdf} className="rounded-none w-full justify-start gap-2" data-testid="download-pdf-btn">
                <Download size={14} strokeWidth={1.5} /> Download PDF report
              </Button>
              <div className="flex gap-2">
                <Input value={email} onChange={(e) => setEmail(e.target.value)} type="email" placeholder="name@brand.com" className="rounded-none flex-1" data-testid="email-input" />
                <Button onClick={emailReport} disabled={sending} variant="outline" className="rounded-none" data-testid="email-report-btn">
                  <Mail size={14} strokeWidth={1.5} />
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* COUNTS */}
        <div className="mt-10">
          <Counts a={audit} />
        </div>

        <div className="mt-10 grid grid-cols-1 lg:grid-cols-3 gap-px bg-border">
          <div className="bg-background p-6">
            <div className="label-mono mb-3">Panels detected</div>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span>PDP page</span><span className="font-mono">{r.panels?.PDP_page ?? '—'}</span></div>
              <div className="flex justify-between"><span>BOP page</span><span className="font-mono">{r.panels?.BOP_page ?? '—'}</span></div>
              <div className="flex justify-between"><span>PDP area (cm²)</span><span className="font-mono">{r.panels?.PDP_estimated_area_cm2 ?? '—'}</span></div>
            </div>
            <p className="mt-4 text-xs text-muted-foreground leading-relaxed">{r.panels?.identification_basis}</p>
          </div>
          <div className="bg-background p-6">
            <div className="label-mono mb-3">Slug reading</div>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span>Found</span><span className="font-mono">{r.slug?.found ? 'Yes' : 'No'}</span></div>
              <div className="flex justify-between"><span>Material</span><span className="font-mono text-right">{r.slug?.packaging_material || '—'}</span></div>
              <div className="flex justify-between"><span>Print process</span><span className="font-mono">{r.slug?.print_process || '—'}</span></div>
              <div className="flex justify-between"><span>Dieline ver.</span><span className="font-mono">{r.slug?.dieline_version || '—'}</span></div>
            </div>
          </div>
          <div className="bg-background p-6">
            <div className="label-mono mb-3">Keyline structure</div>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span>Detected</span><span className="font-mono">{r.keyline?.detected ? 'Yes' : 'No'}</span></div>
              <div className="flex justify-between"><span>Bleed</span><span className="font-mono">{String(r.keyline?.bleed_present || '—')}</span></div>
              <div className="flex justify-between"><span>Safe area</span><span className="font-mono">{String(r.keyline?.safe_area_visible || '—')}</span></div>
            </div>
            {!!(r.keyline?.structural_issues?.length) && (
              <ul className="mt-3 list-disc list-inside text-xs text-severity-high">
                {r.keyline.structural_issues.map((i, idx) => <li key={idx}>{i}</li>)}
              </ul>
            )}
          </div>
        </div>

        {/* TABS */}
        <div className="mt-12">
          <Tabs defaultValue="flags" className="w-full">
            <TabsList className="rounded-none border border-border bg-transparent p-0 h-auto">
              {[
                ['flags', `Findings (${flags.length})`],
                ['mandatory', 'Mandatory fields'],
                ['passed', `Passed (${passed.length})`],
                ['nomenclature', 'Nomenclature'],
                ['material', 'Material compliance'],
              ].map(([v, l]) => (
                <TabsTrigger key={v} value={v} className="rounded-none border-r border-border last:border-r-0 px-5 py-3 text-sm data-[state=active]:bg-foreground data-[state=active]:text-background" data-testid={`tab-${v}`}>{l}</TabsTrigger>
              ))}
            </TabsList>

            <TabsContent value="flags" className="mt-6">
              <div className="border border-border">
                <div className="grid grid-cols-12 px-4 py-3 bg-surface label-mono">
                  <div className="col-span-2">Severity</div>
                  <div className="col-span-2">Check</div>
                  <div className="col-span-4">Finding</div>
                  <div className="col-span-3">Suggestion</div>
                  <div className="col-span-1 text-right">Page</div>
                </div>
                {flags.length === 0 && <div className="p-8 text-sm text-muted-foreground">No flags raised.</div>}
                {flags.map((f, i) => (
                  <div key={i} className="grid grid-cols-12 px-4 py-4 border-t border-border text-sm items-start" data-testid={`flag-row-${i}`}>
                    <div className="col-span-2"><SeverityBadge severity={f.severity} /></div>
                    <div className="col-span-2 space-y-1">
                      <div className="font-mono text-xs">{f.check_id}</div>
                      <div className="text-xs text-muted-foreground">{f.category} · {f.panel}</div>
                    </div>
                    <div className="col-span-4 space-y-1">
                      <div>{f.finding}</div>
                      {f.regulation_ref && <div className="text-xs text-muted-foreground">{f.regulation_ref}</div>}
                    </div>
                    <div className="col-span-3 text-sm">{f.suggestion}</div>
                    <div className="col-span-1 text-right font-mono text-xs">{f.page_reference ?? '—'}</div>
                  </div>
                ))}
              </div>
            </TabsContent>

            <TabsContent value="mandatory" className="mt-6">
              <div className="border border-border">
                <div className="px-4 py-3 bg-surface label-mono">Mandatory fields status</div>
                <div className="px-4">
                  <MandatoryRow label="Product name" status={m.product_name} />
                  <MandatoryRow label="Generic name" status={m.generic_name} />
                  <MandatoryRow label="Ingredients list" status={m.ingredients_list} />
                  <MandatoryRow label="Nutrition panel" status={m.nutrition_panel} />
                  <MandatoryRow label="FSSAI number" status={m.fssai_number} />
                  <MandatoryRow label="Veg/Non-veg symbol" status={m.veg_nonveg_symbol} />
                  <MandatoryRow label="Date marking" status={m.date_marking} />
                  <MandatoryRow label="Batch number" status={m.batch_number} />
                  <MandatoryRow label="Manufacturer address" status={m.manufacturer_address} />
                  <MandatoryRow label="Net quantity" status={m.net_quantity} />
                  <MandatoryRow label="MRP" status={m.mrp} />
                  <MandatoryRow label="Storage conditions" status={m.storage_conditions} />
                  <MandatoryRow label="Country of origin" status={m.country_of_origin} />
                </div>
              </div>
            </TabsContent>

            <TabsContent value="passed" className="mt-6">
              <div className="border border-border">
                <div className="px-4 py-3 bg-surface label-mono">Passed checks</div>
                {passed.length === 0 && <div className="p-8 text-sm text-muted-foreground">No passed checks recorded.</div>}
                {passed.map((p, i) => (
                  <div key={i} className="flex items-center justify-between px-4 py-3 border-t border-border text-sm">
                    <div className="flex items-center gap-3"><CheckCircle2 size={14} className="text-severity-pass" /><span>{p.description}</span></div>
                    <span className="font-mono text-xs text-muted-foreground">{p.check_id} · {p.panel}</span>
                  </div>
                ))}
              </div>
            </TabsContent>

            <TabsContent value="nomenclature" className="mt-6">
              <div className="panel p-6 space-y-4 text-sm">
                <div className="flex justify-between"><span>Generic name detected</span><span className="font-mono">{r.generic_name_detected || '—'}</span></div>
                <div className="flex justify-between"><span>Brand name detected</span><span className="font-mono">{r.brand_name_detected || '—'}</span></div>
                <div className="flex justify-between"><span>Generic name correct</span><span className="font-mono">{String(r.nomenclature_assessment?.generic_name_correct ?? '—')}</span></div>
                <div className="flex justify-between"><span>Brand name misleading</span><span className="font-mono">{String(r.nomenclature_assessment?.brand_name_misleading ?? '—')}</span></div>
                <div className="flex justify-between"><span>Flavour qualifier required</span><span className="font-mono">{String(r.nomenclature_assessment?.flavour_qualifier_required ?? '—')}</span></div>
                <div className="flex justify-between"><span>Disclaimer required</span><span className="font-mono">{String(r.nomenclature_assessment?.disclaimer_required ?? '—')}</span></div>
                {r.nomenclature_assessment?.notes && <p className="text-muted-foreground border-t border-border pt-3">{r.nomenclature_assessment.notes}</p>}
              </div>
            </TabsContent>

            <TabsContent value="material" className="mt-6">
              <div className="panel p-6 space-y-3 text-sm">
                <div className="flex justify-between"><span>Material identified</span><span className="font-mono text-right">{r.packaging_material_compliance?.material_identified || '—'}</span></div>
                <div className="flex justify-between"><span>Compliance status</span><span className="font-mono">{r.packaging_material_compliance?.compliance_status || 'UNKNOWN'}</span></div>
                <p className="text-muted-foreground border-t border-border pt-3">{r.packaging_material_compliance?.notes}</p>
              </div>
            </TabsContent>
          </Tabs>
        </div>

        {/* DISCLAIMER */}
        <div className="mt-16 border border-border p-8" data-testid="disclaimer">
          <div className="label-mono mb-3">Legal disclaimer</div>
          <p className="text-xs text-muted-foreground leading-relaxed max-w-4xl">
            PackCheck is an automated compliance assistance tool. This audit is generated by an AI system and is based on automated
            analysis of the uploaded artwork. It does not constitute legal advice, regulatory opinion, or professional compliance
            certification. Findings are indicative only and not exhaustive or guaranteed accurate in all circumstances. The Food
            Business Operator and their authorised regulatory representative remain solely responsible for verifying compliance with
            all applicable food safety, labelling, packaging and other laws before printing, launching, or distributing any food product.
          </p>
        </div>
      </div>
    </Layout>
  );
}
